import { DeviceDetailsResponse } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/aqora_service_pb';
import {
  Connectivity,
  Connectivity_Status,
} from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/connectivity_pb';
import { Link, Theme } from '@mui/material';
import { TFunction } from 'i18next';
import { timestampDate } from '@bufbuild/protobuf/wkt';
import { assertIsDefined } from '~/types/assert-type';
import { DeviceSpecificCommandModel } from '~/shared/models/system/DeviceSpecificCommandModel';

export type Detail = {
  label?: string;
  value?: React.ReactNode;
  color?: string;
};

export class DeviceDetailsModel {
  readonly connectivity?: Connectivity;

  readonly serialNumber?: string;

  readonly externalLink?: string;

  readonly externalLinkText?: string;

  readonly externalLinkLabel?: string;

  readonly isFromParticleIo: boolean;

  readonly deviceSpecificCommands?: DeviceSpecificCommandModel[];

  constructor(deviceDetailsResponse?: DeviceDetailsResponse | null) {
    this.connectivity = deviceDetailsResponse?.connectivity;

    this.serialNumber = deviceDetailsResponse?.serialNumber;

    this.externalLink = deviceDetailsResponse?.externalLink;

    this.externalLinkText = deviceDetailsResponse?.externalLinkText;

    this.externalLinkLabel = deviceDetailsResponse?.externalLinkLabel;

    this.isFromParticleIo =
      deviceDetailsResponse?.connectivity?.status !==
      Connectivity_Status.UNSPECIFIED;

    this.deviceSpecificCommands =
      deviceDetailsResponse?.availableDeviceSpecificCommand?.map(
        (commandSpec) => new DeviceSpecificCommandModel(commandSpec),
      );
  }

  getConnectivityDetails(theme: Theme, t: TFunction): Detail[] {
    assertIsDefined(this.connectivity);

    const {
      latestMetricsReceived,
      status,
      batteryPercentage,
      signalStrengthPercentage,
      signalStrengthDbm,
      network,
    } = this.connectivity;

    const details: Detail[] = [
      {
        label: t('device.connectivity.labels.latestDataDate'),
        value: latestMetricsReceived
          ? timestampDate(latestMetricsReceived).toUTCString()
          : 'unknown',
      },
    ];

    if (!this.isFromParticleIo) {
      return details;
    }

    details.push(
      {
        label: t('device.connectivity.labels.status'),
        value: DeviceDetailsModel.getConnectivityStatus(status, theme).label,
        color: DeviceDetailsModel.getConnectivityStatus(status, theme).color,
      },
      {
        label: t('device.connectivity.labels.battery'),
        value: `${batteryPercentage}%`,
      },
      {
        label: t('device.connectivity.labels.signalStrenth'),
        value: `${signalStrengthPercentage}%`,
      },
      {
        label: t('device.connectivity.labels.signalStrenthDbm'),
        value: `${Number(signalStrengthDbm)} dBm`,
      },
      {
        label: t('device.connectivity.labels.network'),
        value: network,
      },
    );

    return details;
  }

  getHardwareInfo(t: TFunction): Detail[] {
    return [
      {
        label: t('device.hardware.labels.serial'),
        value: this.serialNumber || '---',
      },
      {
        label: this.externalLinkLabel,
        value: (
          <Link
            href={this.externalLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {this.externalLinkText}
          </Link>
        ),
      },
    ];
  }

  private static getConnectivityStatus = (
    ConnectivityStatus: Connectivity_Status,
    theme: Theme,
  ) => {
    switch (ConnectivityStatus) {
      case Connectivity_Status.OK:
        return {
          label: 'OK',
          color: theme.palette.success.main,
        };
      default:
        return {
          label: 'Unknown',
          color: theme.palette.error.main,
        };
    }
  };
}
